import { FC } from "react";
import { useTranslation } from "react-i18next";
import { FILE_URL, Suspect } from "types";
import { constructFileUrl, DateView } from "utils/helper";

type Props = {
  suspect: Suspect | undefined;
  isOpen: boolean;
  isDefendant?: boolean;
  onClose: () => void;
};

const SuspectDetailInfo: FC<Props> = ({
  suspect,
  isOpen,
  isDefendant = false,
  onClose,
}) => {
  const { t } = useTranslation();

  const handleClickOutside = (e: any) => {
    if (e.target.classList.contains("fixed")) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      style={{ marginTop: "0px" }}
      className="fixed inset-0 z-50 flex justify-end bg-black bg-opacity-50 backdrop-blur-sm p-4"
      onClick={handleClickOutside}
    >
      <div className="relative w-full max-w-2xl bg-white p-8 shadow-lg rounded-lg overflow-y-auto">
        <div className="text-base font-semibold text-white bg-[#212121] p-3 inline-block rounded-md">
          {isDefendant ? t("defendant_id") : t("suspect_id")} &nbsp;&nbsp; :
          &nbsp;&nbsp;
          {suspect?.identity_card_number}
        </div>

        <br />

        <div className="flex flex-row gap-4 mt-3 items-start">
          <div className="bg-[#BCBCBC66] rounded-md">
            <img
              // src={
              //   suspect?.picture_file ||
              //   require("../../../../images/imageVector.png")
              // }
              src={(() => {
                const doc = suspect?.documents?.find(
                  (doc) => doc.document_type === "suspect_picture"
                );
                return doc
                  ? constructFileUrl(doc, FILE_URL.SUSPECT)
                  : require("../../../../images/imageVector.png");
              })()}
              alt={t("suspect")}
              className="w-auto h-[110px] rounded"
            />
          </div>
          <div className="flex flex-col w-[250px]">
            <div className="bg-[#9B0505] w-fit px-2 py-[2px] text-white rounded-md text-center font-medium text-sm">
              {suspect?.CR_number}
            </div>
            <h2 className="text-[22px] font-semibold leading-7">
              {suspect?.first_name} {suspect?.middle_name} {suspect?.last_name}
            </h2>
            <p className="text-base font-normal text-[#8B8B8B]">
              {suspect?.phone_number}
            </p>
            <p className="text-base font-normal text-[#8B8B8B]">
              {suspect?.email}
            </p>
          </div>
        </div>

        {[
          { property: t("gender"), value: suspect?.gender },
          { property: t("age"), value: suspect?.age },
          { property: t("education_level"), value: suspect?.education_level },
          { property: t("job_position"), value: suspect?.job_position },
          { property: t("ethnicity"), value: suspect?.ethnicity },
          { property: t("nationality"), value: suspect?.nationality },
          { property: t("religion"), value: suspect?.religion },
          {
            property: t("date_of_birth"),
            value: DateView(suspect?.date_of_birth),
          },
          { property: t("phone_number"), value: suspect?.phone_number },
          { property: t("email"), value: suspect?.email },
          {
            property: t("identity_card_number"),
            value: suspect?.identity_card_number,
          },
          { property: t("criminal_record"), value: suspect?.criminal_record },
          { property: t("statement"), value: suspect?.statement },
          { property: t("is_in_custody"), value: suspect?.is_in_custody },
        ]?.map((info, index) => (
          <div
            key={index}
            className="flex flex-row gap-4 mt-3 items-start justify-between border-b border-b-[#E9E9E9] pb-3"
          >
            <p className="font-normal text-base text-[#0A1D39]">
              {info?.property}
            </p>
            <p className="font-normal text-base text-[#0A1D39]">
              {typeof info?.value === "boolean" ||
              typeof info?.value === "object"
                ? (info?.value instanceof Boolean
                    ? info?.value.valueOf()
                    : info?.value) ?? t("na")
                : info?.value}
            </p>
          </div>
        ))}

        <div className="bg-[#F8F8F8] p-3 rounded-xl mt-5">
          <p className="text-base font-semibold">{t("address_location")}</p>
          <hr className="text-[#8B8B8B] my-3 w-[50%]" />

          <div className="grid grid-cols-2 gap-2 gap-x-4 mt-4">
            {[
              { property: t("region"), value: suspect?.region },
              { property: t("zone"), value: suspect?.zone },
              { property: t("woreda"), value: suspect?.woreda },
              { property: t("kebele"), value: suspect?.kebele },
              { property: t("house_number"), value: suspect?.house_number },
              { property: t("city"), value: suspect?.city },
            ]?.map((info, index) => (
              <div key={index}>
                <p className="font-normal text-sm text-[#747171]">
                  {info.property}
                </p>
                <p className="font-semibold text-base text-[#0A1D39]">
                  {info.value}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SuspectDetailInfo;
